import { CardHeader } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import ProfileBubble from '@components/ProfileBubble';

import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import type { PatientRecord } from '@models/patient';

import { formatName } from '@utils/format';
import { isDependant, isPatient as isPatientUser } from '@utils/role';

import type { GetUserRecordQuery } from 'generated/user.generated';

const messages = defineMessages({
  clientType: {
    id: 'clientProfileCard.clientType',
    defaultMessage: 'Client ({patientType})',
  },
  dependant: {
    id: 'GLOBAL.dependant',
    defaultMessage: 'Dependant',
  },
  accountHolder: {
    id: 'GLOBAL.accountHolder',
    defaultMessage: 'Account holder',
  },
  patient: {
    id: 'GLOBAL.patient',
    defaultMessage: 'Patient',
  },
});

interface DemographicsCardHeaderProps {
  patient: PatientRecord | null;
  user?: GetUserRecordQuery['getUserRecord'];
  is_owner: boolean;
}

export default function DemographicsCardHeader({
  patient,
  user,
  is_owner,
}: DemographicsCardHeaderProps) {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile, isCurator, isPractitioner },
  } = useSession();
  const {
    state: { isEditing },
    startEditingRecord,
    stopEditingRecord,
  } = useCuration();

  const name = formatName(user?.first_name, user?.last_name);
  const isPatient = Boolean(user?.vitall_patient?.patient_id);

  const dataKey = JSON.stringify(user?.roles);
  const subtitle = React.useMemo(() => {
    if (!activeProfile) {
      return '';
    }
    const label = [
      ...(is_owner ? [formatMessage(messages.accountHolder)] : []),
      ...[
        isPatientUser(activeProfile)
          ? `${
              isDependant(activeProfile)
                ? formatMessage(messages.dependant)
                : ''
            } ${formatMessage(messages.patient)}`
          : '',
      ],
    ].join(', ');
    return label;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, is_owner]);

  const handleEdit = () =>
    isEditing ? stopEditingRecord() : startEditingRecord();

  return (
    <CardHeader
      action={
        patient && (
          <Icon
            clickable
            color="text.secondary"
            name={isEditing ? 'xmark' : 'pencil'}
            onClick={handleEdit}
            size="lg"
            type="fas"
          />
        )
      }
      avatar={
        <ProfileBubble fallback="circle-user" s3Key={user?.profile_picture} />
      }
      subheader={
        isCurator
          ? formatMessage(messages.clientType, {
              patientType: isPatient ? 'Patient' : 'Non Patient',
            })
          : isPractitioner
            ? subtitle
            : null
      }
      title={name ?? '-'}
      slotProps={{
        title: {
          sx: {
            color: (theme) => theme.palette.primary.main,
          },
        },
      }}
    />
  );
}
